import { useState, useRef, useEffect, useMemo } from "react";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import api from "../../../utils/api";
import type { YardInspection } from "../../../store/oasis/yardInspectionsStore";
import {
  fetchInspectionGeofenceEvent,
  WATER_PARAMETERS,
  type InspectionGeofenceEvent,
  type WaterParameterKey,
} from "../../../store/oasis/yardInspectionsStore";
import type { TruckYardVehicle } from "../../../store/truckYardStore";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { useTruckYardStore } from "../../../store/truckYardStore";
import { useUserStore } from "../../../store/userStore";
import { useGroupsStore } from "../../../store/admin/groupsStore";
import { useAllGroups } from "../../../features/catalogs/queries";
import {
  fetchYardAccessEvent,
  type YardAccessEvent,
} from "../../../store/oasis/hikvisionAccessStore";
import {
  WATER_TYPES,
  getWaterTypeLabel,
  effectiveWaterTypes,
} from "../../patios-maniobras/components/waterTypes";

// Minimal shape of the lite-events API rows we care about for the checker
// cards. Kept local to avoid coupling this modal to the full lite events
// store interface (we read directly from the API and don't dispatch to it).
interface LiteVehicleEvent {
  id: string;
  vehicle_id: string;
  latitude: number;
  longitude: number;
  dateTime: { _seconds: number; _nanoseconds: number } | string;
  address?: string;
  eventuality?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;
    if (typeof obj._seconds === "number")
      return (obj._seconds as number) * 1000;
    if (typeof obj.seconds === "number") return (obj.seconds as number) * 1000;
  }
  return 0;
}

function generateFolio(inspection: YardInspection) {
  // Consecutive folio assigned at creation — the authoritative number.
  if (inspection.folio != null) {
    return `INS-${String(inspection.folio).padStart(6, "0")}`;
  }
  // Legacy fallback for inspections created before consecutive folios existed.
  const baseMs =
    toMillis(inspection.createdAt) ||
    toMillis(inspection.entry_timestamp) ||
    Date.now();
  const d = new Date(baseMs);
  return `INS-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${inspection.id.slice(-4).toUpperCase()}`;
}

function formatDateTimeLocal(ms: number) {
  const d = new Date(ms || Date.now());
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Human-readable time gap between a geofence crossing and its access event.
// `deltaMs` is (geofence crossing time − access event time): positive means the
// geocerca was crossed *after* the gate scan, negative *before*.
function formatAccessDelta(deltaMs: number): string {
  const totalSec = Math.round(Math.abs(deltaMs) / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  const magnitude = m > 0 ? `${m}m ${s}s` : `${s}s`;
  if (totalSec === 0) return "Simultáneo al acceso";
  return `Δ ${magnitude} ${deltaMs > 0 ? "después" : "antes"} del acceso`;
}

function liteEventMillis(ev: LiteVehicleEvent): number {
  if (typeof ev.dateTime === "string") {
    const d = new Date(ev.dateTime);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (ev.dateTime && typeof ev.dateTime._seconds === "number") {
    return ev.dateTime._seconds * 1000;
  }
  return 0;
}

// Tamaño de la página que se le pide a /eventList. Ver el efecto que la usa:
// el endpoint no filtra por vehículo, así que de estas N filas sólo unas pocas
// son de la unidad del reporte.
const EVENT_FEED_LIMIT = 500;

// ─── Component ───────────────────────────────────────────────────────────────

export default function InspectionReportModal({
  inspection,
  vehicle,
  onClose,
}: {
  inspection: YardInspection;
  vehicle: TruckYardVehicle | null;
  onClose: () => void;
}) {
  const { isDarkMode } = useDarkMode();
  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const cardBg = isDarkMode ? "bg-dark-card" : "bg-white";
  const border = isDarkMode ? "border-dark-border" : "border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const textSec = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inp = `w-full px-3 py-2 ${surface} border border-transparent rounded-lg ${text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed`;
  const inpPhoto = `w-full px-2.5 py-1.5 text-sm rounded-lg outline-none ${text} ${isDarkMode ? "bg-gray-700" : "bg-white"} border ${border} disabled:opacity-60 disabled:cursor-not-allowed`;
  const lbl = `text-[11px] font-semibold uppercase tracking-wide ${textSec} mb-1 block`;

  // ── Company
  const companyId = useCompaniesStore((state) => state.companyId);
  const fetchCompany = useCompaniesStore((state) => state.fetchCompany);
  const [companyName, setCompanyName] = useState<string | null>(null);

  // ── Yards (Patios) — preselect this inspection's yard if known
  const yards = useTruckYardStore((state) => state.yards);
  const fetchYards = useTruckYardStore((state) => state.fetchYards);
  const [selectedYardId, setSelectedYardId] = useState<string>(
    inspection.yard_id || "",
  );
  const [yardsLoading, setYardsLoading] = useState(false);

  // ── Logos
  const [logoLeft, setLogoLeft] = useState<string | null>(null);
  const [logoRight, setLogoRight] = useState<string | null>(null);
  const [logoLeftPreview, setLogoLeftPreview] = useState<string | null>(null);
  const [logoRightPreview, setLogoRightPreview] = useState<string | null>(null);
  const refLeft = useRef<HTMLInputElement>(null);
  const refRight = useRef<HTMLInputElement>(null);

  // ── Org
  const [orgName, setOrgName] = useState("SISTEMA DE INSPECCIONES");
  const [orgDescription, setOrgDescription] = useState(
    "REPORTE DE INSPECCIÓN VEHICULAR",
  );

  // ── Groups — used to inherit the org name from the vehicle's group when set.
  // Backend resolves group.vehicle_ids from devices_ids; join on the inspection's
  // vehicle_id. When the vehicle belongs to a group, its name takes precedence
  // over the yard/company fallbacks below.
  const groups = useGroupsStore((state) => state.groups);
  useAllGroups();
  const vehicleGroup = useMemo(() => {
    const vehicleId = vehicle?.id || inspection.vehicle_id;
    if (!vehicleId) return null;
    return (
      groups.find((g) => (g.vehicle_ids || []).includes(vehicleId)) || null
    );
  }, [groups, vehicle?.id, inspection.vehicle_id]);
  // Mirror vehicleGroup into a ref so async resolutions of yard/company
  // fetches (whose closures captured a stale `vehicleGroup`) can defer to
  // the most up-to-date value when deciding whether to overwrite orgName.
  const vehicleGroupRef = useRef(vehicleGroup);
  useEffect(() => {
    vehicleGroupRef.current = vehicleGroup;
  }, [vehicleGroup]);
  useEffect(() => {
    if (vehicleGroup?.name) {
      setOrgName(vehicleGroup.name.toUpperCase());
    }
  }, [vehicleGroup?.id, vehicleGroup?.name]);

  // ── Fetch yards on mount
  useEffect(() => {
    if (yards.length === 0) {
      setYardsLoading(true);
      fetchYards().finally(() => setYardsLoading(false));
    }
  }, [yards.length, fetchYards]);

  // ── Checker cards data ─────────────────────────────────────────────────────
  // We hydrate three pieces of data once on mount:
  //  - Recent lite events for the vehicle (used for the geolocation card and
  //    to count alerts near each access event).
  //  - The full YardAccessEvent records for entry/exit, so we can show their
  //    result (granted/denied/unknown) alongside the timestamps.
  // Errors are swallowed — the cards degrade to "Sin datos" rather than
  // blocking the PDF flow.
  const [vehicleEvents, setVehicleEvents] = useState<LiteVehicleEvent[]>([]);
  // Hasta dónde hacia atrás alcanza el feed cargado. `null` = todavía no se
  // cargó; `0` = el feed es completo (no se truncó) y cubre cualquier fecha.
  // Ver el efecto de abajo: sin esto, "0 alertas" no se distingue de
  // "no se cargó nada de esa fecha", y la diferencia se imprime en un PDF.
  const [eventsCoverageFromMs, setEventsCoverageFromMs] = useState<
    number | null
  >(null);
  const [entryAccessEvent, setEntryAccessEvent] =
    useState<YardAccessEvent | null>(null);
  const [exitAccessEvent, setExitAccessEvent] =
    useState<YardAccessEvent | null>(null);
  // Geofence entry/exit alert linked to this inspection. This is the
  // authoritative position of the vehicle when it crossed the truck-yard
  // geocerca (captured by the geofence monitor), so we prefer it over the
  // nearest-telemetry fallback for the geolocation card below.
  const [geofenceEvent, setGeofenceEvent] =
    useState<InspectionGeofenceEvent | null>(null);
  const [geofenceLoading, setGeofenceLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setGeofenceLoading(true);
    setGeofenceEvent(null);
    fetchInspectionGeofenceEvent(inspection.id)
      .then((res) => {
        if (!cancelled) setGeofenceEvent(res.data);
      })
      .catch(() => {
        if (!cancelled) setGeofenceEvent(null);
      })
      .finally(() => {
        if (!cancelled) setGeofenceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inspection.id]);
  // `/eventList` pide una página y devuelve las más recientes. Dos cosas hay
  // que corregir del lado del cliente, y ninguna es opcional para un dato que
  // termina impreso:
  //
  //  · No filtra por vehículo. `getEventList` (eventController.js:15-59) sólo
  //    mira event_type_id, event_code_id y geofence_id; el `vehicle_id` que se
  //    manda se ignora, así que la respuesta son los eventos más recientes de
  //    TODA la flota. Contando sobre eso, "alertas del vehículo" sumaba las de
  //    cualquier unidad que hubiera reportado en el mismo ±5 min.
  //
  //  · Se trunca. Si hay más páginas, el feed no llega más atrás que su evento
  //    más viejo: para una inspección anterior a esa marca el conteo daba 0
  //    por falta de datos, y se pintaba como una palomita verde de "0 alertas"
  //    — una afirmación, no una ausencia. `eventsCoverageFromMs` guarda ese
  //    piso para que el badge diga "Sin datos" en vez de mentir.
  //
  // El límite se sube a EVENT_FEED_LIMIT (el feed es global, así que cada
  // página rinde menos por vehículo de lo que aparenta) y se deja en una sola
  // llamada: sin filtro por vehículo ni por fecha en el backend, paginar hacia
  // atrás no acota nada, sólo mueve el piso. El arreglo de fondo es que el
  // endpoint acepte vehicle_id y un rango — vive en el módulo `event`, no acá.
  useEffect(() => {
    const vehicleId = vehicle?.id || inspection.vehicle_id;
    if (!vehicleId) return;
    let cancelled = false;
    api
      .get("/eventList", {
        params: { vehicle_id: vehicleId, limit: EVENT_FEED_LIMIT, page: 1 },
      })
      .then((res) => {
        if (cancelled) return;
        const feed: LiteVehicleEvent[] = res.data?.data || [];
        const truncated = (res.data?.meta?.totalPages ?? 1) > 1;
        const oldestMs = feed.reduce((min, ev) => {
          const ms = liteEventMillis(ev);
          if (!ms) return min;
          return min === 0 || ms < min ? ms : min;
        }, 0);
        setEventsCoverageFromMs(truncated ? oldestMs : 0);
        setVehicleEvents(feed.filter((ev) => ev.vehicle_id === vehicleId));
      })
      .catch(() => {
        if (!cancelled) {
          setVehicleEvents([]);
          setEventsCoverageFromMs(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [vehicle?.id, inspection.vehicle_id]);
  useEffect(() => {
    if (!inspection.access_entry_id) {
      setEntryAccessEvent(null);
      return;
    }
    let cancelled = false;
    fetchYardAccessEvent(inspection.access_entry_id)
      .then((ev) => {
        if (!cancelled) setEntryAccessEvent(ev);
      })
      .catch(() => {
        if (!cancelled) setEntryAccessEvent(null);
      });
    return () => {
      cancelled = true;
    };
  }, [inspection.access_entry_id]);
  useEffect(() => {
    if (!inspection.access_exit_id) {
      setExitAccessEvent(null);
      return;
    }
    let cancelled = false;
    fetchYardAccessEvent(inspection.access_exit_id)
      .then((ev) => {
        if (!cancelled) setExitAccessEvent(ev);
      })
      .catch(() => {
        if (!cancelled) setExitAccessEvent(null);
      });
    return () => {
      cancelled = true;
    };
  }, [inspection.access_exit_id]);

  // ── Datos del Vehículo (pre-cargados, editables)
  const [tipoVehiculo, setTipoVehiculo] = useState(
    [vehicle?.brand, vehicle?.model].filter(Boolean).join(" ") ||
      vehicle?.type ||
      "",
  );
  const [placas, setPlacas] = useState(
    vehicle?.plate || inspection.vehicle_plate || "",
  );
  const [anioVehiculo, setAnioVehiculo] = useState(
    vehicle?.year ? String(vehicle.year) : "",
  );
  const [tipoCombustible, setTipoCombustible] = useState("");
  const [nombreUnidad, setNombreUnidad] = useState(
    vehicle?.name || inspection.vehicle_plate || "",
  );

  // ── Datos auto-completados (editables) ────────────────────────────────────
  //  Domicilio ← ubicación del patio (geocerca); Empresa transportista ← grupo
  //  del vehículo (razón social); NIS + Capacidad ← vehículo; Volumen extraído +
  //  Agua reportada/extraída ← inspección; Fecha de extracción ← hora de las
  //  imágenes. Los que dependen de datos asíncronos (patio/grupo/fotos) se
  //  rellenan en efectos más abajo, una vez resueltos.
  const [domicilio, setDomicilio] = useState("");
  const [empresaTransportista, setEmpresaTransportista] = useState("");
  const [nis, setNis] = useState(vehicle?.nis || "");
  const [capacidadContenedor, setCapacidadContenedor] = useState(
    vehicle?.capacidad || "",
  );
  const [volumenExtraido, setVolumenExtraido] = useState(
    inspection.volumen_extraido || "",
  );
  const [aguaReportada, setAguaReportada] = useState(
    inspection.water_type_reported || "",
  );
  const [aguaExtraida, setAguaExtraida] = useState(inspection.water_type || "");
  const [fechaExtraccion, setFechaExtraccion] = useState("");
  // Las tres lecturas del medidor (#502). El punto de la issue es que el número
  // deje de vivir en los píxeles de la foto; si el reporte de entrega no los
  // imprime, el operador los sigue leyendo de las imágenes y la mitad del
  // cambio no llega a quien lo pidió. Editables como el resto de esta modal —
  // el reporte se corrige acá sin tocar la inspección— y sin avisos de rango:
  // el aviso pertenece a la captura, no a la impresión de lo ya capturado.
  const [waterValues, setWaterValues] = useState<
    Record<WaterParameterKey, string>
  >(
    () =>
      Object.fromEntries(
        WATER_PARAMETERS.map((p) => [p.key, inspection[p.field] || ""]),
      ) as Record<WaterParameterKey, string>,
  );

  // ── Logged User
  const loggedUser = useUserStore((state) => state.user);

  // ── Datos del Inspector (pre-cargados, editables)
  const [nombreInspector, setNombreInspector] = useState(() => {
    if (inspection.inspector_name) return inspection.inspector_name;
    if (loggedUser?.first_name && loggedUser?.last_name) {
      return `${loggedUser.first_name} ${loggedUser.last_name}`;
    }
    return "";
  });
  const [emailInspector, setEmailInspector] = useState(loggedUser?.email || "");
  const [rolInspector, setRolInspector] = useState(
    loggedUser?.role?.name || "Administrador",
  );

  // ── Datos de la Inspección (pre-cargados, editables)
  const [tituloInspeccion, setTituloInspeccion] = useState(
    inspection.title ||
      `Inspección — ${vehicle?.name || inspection.vehicle_plate || "Unidad"}`,
  );
  const [descripcionInspeccion, setDescripcionInspeccion] = useState(
    inspection.description || "",
  );
  const inspectionMs =
    toMillis(inspection.entry_timestamp) || toMillis(inspection.createdAt);
  const [fechaInspeccion, setFechaInspeccion] = useState(
    formatDateTimeLocal(inspectionMs),
  );
  const [lugarInspeccion, setLugarInspeccion] = useState(
    inspection.yard_name || "",
  );
  const [observaciones, setObservaciones] = useState(inspection.notes || "");

  // ── Fotos (de las evidencias)
  const [photos, setPhotos] = useState(
    (inspection.evidences ?? []).map((e, i) => ({
      id: e.id,
      url: e.imageUrl,
      title: e.title || "",
      description: e.description || "",
      observacion: "",
      photoNumber: e.order ?? i + 1,
      timestamp: toMillis(e.timestamp) || toMillis(e.createdAt) || Date.now(),
    })),
  );

  // ── Derived data for checker cards ────────────────────────────────────────
  const entryMs = toMillis(inspection.entry_timestamp);
  const exitMs = toMillis(inspection.exit_timestamp);

  // Yard coordinates (from the patio inherited via access event) for the
  // dentro/fuera distance badge.
  const inheritedYard = useMemo(
    () => yards.find((y) => y.id === selectedYardId) || null,
    [yards, selectedYardId],
  );

  // Backfill the auto-filled fields once their async sources resolve. Each only
  // overwrites when it has a value, so manual edits to a still-empty field win.
  useEffect(() => {
    if (inheritedYard?.location) setDomicilio(inheritedYard.location);
  }, [inheritedYard?.location]);
  useEffect(() => {
    const t = vehicleGroup?.razonSocial || vehicleGroup?.name || "";
    if (t) setEmpresaTransportista(t);
  }, [vehicleGroup?.id]);
  // Fecha/hora de la extracción ← la foto más antigua de la inspección.
  const extractionMs = useMemo(() => {
    const times = photos.map((p) => p.timestamp).filter(Boolean) as number[];
    return times.length ? Math.min(...times) : null;
  }, [photos]);
  useEffect(() => {
    setFechaExtraccion(extractionMs ? formatDateTimeLocal(extractionMs) : "");
  }, [extractionMs]);

  // ── Ingreso / egreso a la geocerca correlacionados con los accesos ─────────
  // The geofence monitor records when the unit crossed the patio's geocerca
  // (entered_at / exited_at + coordinates). We line each crossing up against the
  // matching gate access event — the ingreso vs the entrada, the egreso vs la
  // salida — so the report can show that the physical badge scan coincided (or
  // how far it was) with the GPS geocerca crossing.
  const MATCH_WINDOW_MS = 5 * 60 * 1000; // ±5 min counts as "coincide"
  const geofenceLegs = useMemo(() => {
    if (!geofenceEvent) return null;
    const buildLeg = (
      coords: { lat: number; lon: number } | null,
      crossingMs: number | null,
      accessMs: number,
    ) => {
      if (!coords && !crossingMs) return null;
      const deltaMs =
        crossingMs && accessMs ? crossingMs - accessMs : null;
      return {
        coords,
        crossingMs: crossingMs || null,
        deltaMs,
        coincides: deltaMs !== null && Math.abs(deltaMs) <= MATCH_WINDOW_MS,
      };
    };
    // Prefer the structured legs (ingreso fixed at creation, egreso resolved on
    // open); fall back to the legacy flat fields for older payloads.
    return {
      name: geofenceEvent.geofence_name,
      ingreso: buildLeg(
        geofenceEvent.ingreso?.coordinates ?? geofenceEvent.enter_coordinates,
        geofenceEvent.ingreso?.entered_at ?? geofenceEvent.entered_at,
        entryMs,
      ),
      egreso: buildLeg(
        geofenceEvent.egreso?.coordinates ?? geofenceEvent.exit_coordinates,
        geofenceEvent.egreso?.exited_at ?? geofenceEvent.exited_at,
        exitMs,
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geofenceEvent, entryMs, exitMs]);

  // Alert proxy: count vehicle telemetry events whose `eventuality` is set
  // (i.e. the device flagged something) within ±5 min of each access event.
  // The YardAccessEvent itself has no alert array; this is the best signal
  // available from the lite events feed.
  const ALERT_WINDOW_MS = 5 * 60 * 1000;
  // `null` = no sabemos: el feed cargado no llega hasta ese instante, así que
  // un 0 sería "no miré", no "no hubo". Se distingue en el badge.
  const countAlertsAround = (eventMs: number): number | null => {
    if (!eventMs) return null;
    if (eventsCoverageFromMs === null) return null;
    const outOfCoverage =
      eventsCoverageFromMs > 0 &&
      eventMs + ALERT_WINDOW_MS < eventsCoverageFromMs;
    if (outOfCoverage) return null;
    return vehicleEvents.reduce((acc, ev) => {
      if (!ev.eventuality) return acc;
      const ms = liteEventMillis(ev);
      if (!ms) return acc;
      return Math.abs(ms - eventMs) <= ALERT_WINDOW_MS ? acc + 1 : acc;
    }, 0);
  };
  const entryAlertsCount = useMemo(
    () => countAlertsAround(entryMs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicleEvents, eventsCoverageFromMs, entryMs],
  );
  const exitAlertsCount = useMemo(
    () => countAlertsAround(exitMs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicleEvents, eventsCoverageFromMs, exitMs],
  );

  // Photo timeframe check: how many uploaded evidences fall within the
  // entry/exit window. If there's no exit yet, we use "now" as the upper
  // bound so an in-progress inspection still validates correctly.
  const photoTimeframeCheck = useMemo(() => {
    const total = photos.length;
    if (total === 0)
      return { total: 0, inRange: 0, outOfRange: 0, hasRange: false };
    if (!entryMs) return { total, inRange: 0, outOfRange: 0, hasRange: false };
    const upperBound = exitMs || Date.now();
    let inRange = 0;
    let outOfRange = 0;
    photos.forEach((p) => {
      if (!p.timestamp) {
        outOfRange++;
        return;
      }
      if (p.timestamp >= entryMs && p.timestamp <= upperBound) inRange++;
      else outOfRange++;
    });
    return { total, inRange, outOfRange, hasRange: true };
  }, [photos, entryMs, exitMs]);

  // ── Comparación tipo de agua ──────────────────────────────────────────────
  // Compare the water type observed in this inspection against the types
  // assigned to the vehicle (group-inherited union ∩ per-vehicle subset).
  //  · match     → green: the inspected water is among the unit's assigned types
  //  · no_unit    → yellow: no vehicle assigned to the inspection
  //  · mismatch   → red: inspected water not accepted by the unit (or the unit
  //                  declares no water types at all)
  //  · no_water   → neutral: no water type recorded on the inspection yet
  const waterComparison = useMemo(() => {
    const inspected = inspection.water_type || null;
    const vehicleId = vehicle?.id || inspection.vehicle_id;

    if (!vehicleId) {
      return { state: "no_unit" as const, inspected, vehicleTypes: [] as string[] };
    }

    const inherited = Array.from(
      new Set(
        groups
          .filter((g) => (g.vehicle_ids || []).includes(vehicleId))
          .flatMap((g) => g.type || []),
      ),
    );
    const vehicleTypes = effectiveWaterTypes(vehicle?.truck_yard_types, inherited);

    if (!inspected) {
      return { state: "no_water" as const, inspected, vehicleTypes };
    }
    const state = vehicleTypes.includes(inspected) ? "match" : "mismatch";
    return { state: state as "match" | "mismatch", inspected, vehicleTypes };
  }, [
    inspection.water_type,
    inspection.vehicle_id,
    vehicle?.id,
    vehicle?.truck_yard_types,
    groups,
  ]);

  // ── Handle yard selection
  const handleYardChange = (yardId: string) => {
    setSelectedYardId(yardId);
    if (!yardId) return;

    const yard = yards.find((y) => y.id === yardId);
    if (yard) {
      if (yard.name && !vehicleGroupRef.current)
        setOrgName(yard.name.toUpperCase());
      if (yard.logo) {
        setLogoLeftPreview(yard.logo);
        fetch(yard.logo)
          .then((res) => res.blob())
          .then((blob) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const result = e.target?.result as string;
              const b64 = result.split(",")[1];
              setLogoLeft(b64);
            };
            reader.readAsDataURL(blob);
          })
          .catch((err) => console.warn("Could not load yard logo:", err));
      } else {
        setLogoLeft(null);
        setLogoLeftPreview(null);
      }
      if (yard.location) setLugarInspeccion(yard.location);
    }
  };

  // ── Apply preselected yard once yards arrive
  useEffect(() => {
    if (selectedYardId && yards.length > 0 && !logoLeftPreview) {
      handleYardChange(selectedYardId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yards.length]);

  // ── Fetch company info on mount (fallback if no yard selected)
  useEffect(() => {
    if (companyId && !selectedYardId) {
      fetchCompany(companyId)
        .then((company) => {
          if (company) {
            if (company.name) {
              if (!vehicleGroupRef.current)
                setOrgName(company.name.toUpperCase());
              setCompanyName(company.name);
            }
            if (company.logo) {
              setLogoLeftPreview(company.logo);
              fetch(company.logo)
                .then((res) => res.blob())
                .then((blob) => {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    const result = e.target?.result as string;
                    const b64 = result.split(",")[1];
                    setLogoLeft(b64);
                  };
                  reader.readAsDataURL(blob);
                })
                .catch((err) =>
                  console.warn("Could not load company logo:", err),
                );
            }
          }
        })
        .catch((err) => console.warn("Could not fetch company:", err));
    }
  }, [companyId, fetchCompany, selectedYardId]);

  const folio = generateFolio(inspection);

  const updatePhoto = (
    i: number,
    field: "title" | "description" | "observacion",
    val: string,
  ) =>
    setPhotos((prev) =>
      prev.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)),
    );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

  const canGenerate = !!tituloInspeccion.trim();

  const handleLogo =
    (side: "left" | "right") => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result as string;
        const b64 = result.split(",")[1];
        if (side === "left") {
          setLogoLeft(b64);
          setLogoLeftPreview(result);
        } else {
          setLogoRight(b64);
          setLogoRightPreview(result);
        }
      };
      reader.readAsDataURL(file);
    };

  const handleGenerate = async (preview = false) => {
    if (!canGenerate) return;
    setLoading(true);
    setError(null);
    setPdfPreviewUrl(null);
    const selectedYard = yards.find((y) => y.id === selectedYardId);
    const payload = {
      companyId: companyId ?? undefined,
      companyName: companyName ?? undefined,
      yardId: selectedYardId || undefined,
      logoLeft: logoLeft ?? undefined,
      logoRight: logoRight ?? undefined,
      logoLeftUrl: selectedYard?.logo || undefined,
      orgName,
      orgDescription,
      folio,
      tituloInspeccion,
      descripcionInspeccion,
      fechaInspeccion,
      lugarInspeccion,
      domicilio,
      empresaTransportista,
      // Folio del permiso de descarga = el PPD del vehículo.
      folioPermiso: vehicle?.ppd || undefined,
      nis,
      capacidadContenedor,
      volumenExtraido,
      fechaExtraccion,
      aguaReportada: aguaReportada
        ? getWaterTypeLabel(aguaReportada)
        : undefined,
      aguaExtraida: aguaExtraida ? getWaterTypeLabel(aguaExtraida) : undefined,
      // Se manda la lista armada, con etiqueta y unidad, y no tres campos
      // sueltos: el PDF imprime lo que reciba, así que un cuarto parámetro en
      // WATER_PARAMETERS aparece en el reporte sin tocar el backend. Los
      // vacíos se filtran acá para que una inspección anterior a #502 —donde
      // los tres están ausentes— imprima exactamente el mismo reporte que hoy.
      parametrosAgua: WATER_PARAMETERS.filter((p) =>
        waterValues[p.key].trim(),
      ).map((p) => ({
        label: p.label,
        valor: waterValues[p.key].trim(),
        unidad: p.unit || undefined,
      })),
      nombreUnidad,
      tipoVehiculo,
      placas,
      anioVehiculo,
      tipoCombustible,
      nombreInspector,
      emailInspector,
      rolInspector,
      observaciones,
      entryTimestamp: inspection.entry_timestamp
        ? new Date(toMillis(inspection.entry_timestamp)).toLocaleString("es-ES")
        : undefined,
      exitTimestamp: inspection.exit_timestamp
        ? new Date(toMillis(inspection.exit_timestamp)).toLocaleString("es-ES")
        : undefined,
      driverName: inspection.driver_name ?? undefined,
      tipoAgua: waterComparison.inspected
        ? getWaterTypeLabel(waterComparison.inspected)
        : undefined,
      tiposAguaUnidad: waterComparison.vehicleTypes.map(getWaterTypeLabel),
      tipoAguaCoincide:
        waterComparison.state === "match"
          ? true
          : waterComparison.state === "mismatch"
            ? false
            : undefined,
      fotos: photos.map((p) => ({
        numero: p.photoNumber,
        titulo: p.title || `Foto ${p.photoNumber}`,
        descripcion: p.description || "",
        observacion: p.observacion || "",
        timestamp: new Date(p.timestamp).toLocaleString("es-ES"),
        url: p.url || "",
      })),
    };
    try {
      const response = await api.post("/inspections/report", payload, {
        responseType: "blob",
      });

      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );

      if (preview) {
        setPdfPreviewUrl(url);
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `inspeccion_${placas || nombreUnidad || inspection.id}_${new Date(
          inspectionMs || Date.now(),
        )
          .toLocaleDateString("es-MX")
          .replace(/\//g, "-")}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      console.error("Error generando PDF:", err);
      let errorMsg =
        "No se pudo generar el reporte. Verifica la conexión con el servidor.";
      if (err.response) {
        if (err.response.status === 404) {
          errorMsg =
            "Endpoint no encontrado. El servidor no tiene el endpoint para generar reportes de inspecciones.";
        } else if (err.response.status === 500) {
          errorMsg = "Error interno del servidor. Contacta al administrador.";
        } else {
          errorMsg = `Error del servidor (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        errorMsg = "No hay respuesta del servidor. Verifica tu conexión.";
      } else {
        errorMsg = `Error: ${err.message}`;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const Divider = () => (
    <div
      className={`border-t-2 ${isDarkMode ? "border-gray-600" : "border-gray-800"} my-5`}
    />
  );
  const ThinDivider = () => <div className={`border-t ${border} my-4`} />;
  const SecLabel = ({ children }: { children: React.ReactNode }) => (
    <p
      className={`text-[10px] uppercase tracking-widest font-bold ${textSec} mb-3`}
    >
      {children}
    </p>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
    >
      <div
        className={`${cardBg} border ${border} rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${border} flex-shrink-0`}
        >
          <div>
            <p className={`text-xs ${textSec}`}>
              Inspecciones · {nombreUnidad || "Unidad"}
            </p>
            <h2 className={`text-base font-bold ${text}`}>
              Generar Reporte PDF
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {photos.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  document
                    .getElementById("verificacion-imagenes")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                title="Ir a la verificación de imágenes"
                className="inline-flex items-center gap-1.5 rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs font-medium text-primary-500 hover:bg-primary-500/20 transition-colors"
              >
                <i className="ri-image-2-line"></i>
                <span className="hidden sm:inline">Verificación de imágenes</span>
                <span className="font-bold">{photos.length}</span>
              </button>
            )}
            <button
              aria-label="Cerrar"
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ── CHECKER CARDS ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {/* 1) Geocerca — ingresos / egresos correlacionados con accesos
                   (ocupa el lugar de la antigua tarjeta de Geolocalización) */}
            <div className={`p-4 rounded-xl border ${border} ${surface}`}>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-road-map-line text-primary-500" />
                <p
                  className={`text-[10px] uppercase tracking-widest font-bold ${textSec}`}
                >
                  Geocerca — Ingresos / Egresos
                </p>
              </div>
              {geofenceLoading && !geofenceLegs ? (
                <p className={`text-xs italic ${textSec}`}>
                  Cargando geocerca…
                </p>
              ) : geofenceLegs &&
                (geofenceLegs.ingreso || geofenceLegs.egreso) ? (
                <>
                  {geofenceLegs.name && (
                    <p
                      className={`text-xs ${textSec} mb-2 line-clamp-1`}
                      title={geofenceLegs.name}
                    >
                      <i className="ri-shield-check-line mr-1" />
                      {geofenceLegs.name}
                    </p>
                  )}
                  <div className="space-y-3">
                    {(
                      [
                        {
                          kind: "ingreso" as const,
                          label: "Ingreso",
                          leg: geofenceLegs.ingreso,
                        },
                        {
                          kind: "egreso" as const,
                          label: "Egreso",
                          leg: geofenceLegs.egreso,
                        },
                      ]
                    ).map(({ kind, label, leg }) => (
                      <div key={kind}>
                        <p
                          className={`text-[10px] uppercase ${textSec} font-semibold`}
                        >
                          {label}
                        </p>
                        {leg ? (
                          <>
                            {leg.coords && (
                              <p className={`text-sm font-mono ${text}`}>
                                {leg.coords.lat.toFixed(5)},{" "}
                                {leg.coords.lon.toFixed(5)}
                              </p>
                            )}
                            {leg.crossingMs && (
                              <p className={`text-[10px] ${textSec}`}>
                                <i className="ri-time-line mr-1" />
                                {new Date(leg.crossingMs).toLocaleString(
                                  "es-MX",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </p>
                            )}
                            {leg.deltaMs !== null ? (
                              <span
                                className={`mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${
                                  leg.coincides
                                    ? "bg-green-500/15 text-green-400"
                                    : "bg-yellow-500/15 text-yellow-400"
                                }`}
                              >
                                <i
                                  className={
                                    leg.coincides
                                      ? "ri-check-line"
                                      : "ri-time-line"
                                  }
                                />
                                {leg.coincides
                                  ? "Coincide con el acceso"
                                  : formatAccessDelta(leg.deltaMs)}
                              </span>
                            ) : (
                              <span
                                className={`mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-500/15 ${textSec}`}
                              >
                                <i className="ri-subtract-line" />
                                Sin acceso para comparar
                              </span>
                            )}
                          </>
                        ) : (
                          <p className={`text-sm italic ${textSec}`}>
                            {kind === "ingreso"
                              ? "Sin ingreso registrado"
                              : "Sin egreso registrado"}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className={`text-sm italic ${textSec}`}>
                  Sin cruces de geocerca
                </p>
              )}
            </div>

            {/* 2) Entrada / Salida con conteo de alertas */}
            <div className={`p-4 rounded-xl border ${border} ${surface}`}>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-shield-keyhole-line text-primary-500" />
                <p
                  className={`text-[10px] uppercase tracking-widest font-bold ${textSec}`}
                >
                  Eventos de Acceso
                </p>
              </div>
              <div className="space-y-2">
                {(["entry", "exit"] as const).map((kind) => {
                  const ts =
                    kind === "entry"
                      ? inspection.entry_timestamp
                      : inspection.exit_timestamp;
                  const ms = kind === "entry" ? entryMs : exitMs;
                  const accessEv =
                    kind === "entry" ? entryAccessEvent : exitAccessEvent;
                  const alertsCount =
                    kind === "entry" ? entryAlertsCount : exitAlertsCount;
                  const label = kind === "entry" ? "Entrada" : "Salida";
                  return (
                    <div
                      key={kind}
                      className="flex items-start justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <p
                          className={`text-[10px] uppercase ${textSec} font-semibold`}
                        >
                          {label}
                        </p>
                        <p className={`text-sm ${text}`}>
                          {ts
                            ? new Date(ms).toLocaleTimeString("es-MX", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : kind === "entry"
                              ? "—"
                              : "Pendiente"}
                        </p>
                        {accessEv && (
                          <p
                            className={`text-[10px] ${
                              accessEv.event_result === "granted"
                                ? "text-green-400"
                                : accessEv.event_result === "denied"
                                  ? "text-red-400"
                                  : textSec
                            }`}
                          >
                            {accessEv.event_result}
                          </p>
                        )}
                      </div>
                      {/* Tres estados, no dos. La palomita verde afirma que no
                          hubo alertas; sólo se puede pintar cuando el feed
                          cargado cubre ese instante. Si no lo cubre, el badge
                          es neutro y lo dice. */}
                      {ts && (
                        <span
                          className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                            alertsCount === null
                              ? "bg-gray-500/15 text-gray-400"
                              : alertsCount > 0
                                ? "bg-red-500/15 text-red-400"
                                : "bg-green-500/15 text-green-400"
                          }`}
                          title={
                            alertsCount === null
                              ? "El feed de eventos no alcanza esta fecha: no se puede afirmar que no hubo alertas"
                              : "Alertas dentro de ±5 min del evento"
                          }
                        >
                          <i
                            className={
                              alertsCount === null
                                ? "ri-question-line"
                                : alertsCount > 0
                                  ? "ri-alarm-warning-line"
                                  : "ri-check-line"
                            }
                          />
                          {alertsCount === null
                            ? "Alertas sin datos"
                            : `${alertsCount} alerta${alertsCount === 1 ? "" : "s"}`}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 3) Verificación temporal de las imágenes */}
            <div className={`p-4 rounded-xl border ${border} ${surface}`}>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-camera-line text-primary-500" />
                <p
                  className={`text-[10px] uppercase tracking-widest font-bold ${textSec}`}
                >
                  Verificación de Imágenes
                </p>
              </div>
              {photoTimeframeCheck.total === 0 ? (
                <p className={`text-sm italic ${textSec}`}>Sin fotografías</p>
              ) : !photoTimeframeCheck.hasRange ? (
                <>
                  <p className={`text-2xl font-bold ${text}`}>
                    {photoTimeframeCheck.total}
                  </p>
                  <p className={`text-xs ${textSec} mt-1`}>
                    Sin rango de referencia
                  </p>
                </>
              ) : (
                <>
                  <p className={`text-2xl font-bold ${text}`}>
                    {photoTimeframeCheck.inRange}/{photoTimeframeCheck.total}
                  </p>
                  <p className={`text-xs ${textSec} mt-1`}>
                    {photoTimeframeCheck.outOfRange === 0
                      ? "Todas dentro del rango"
                      : `${photoTimeframeCheck.outOfRange} fuera del rango`}
                  </p>
                  <span
                    className={`mt-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${
                      photoTimeframeCheck.outOfRange === 0
                        ? "bg-green-500/15 text-green-400"
                        : "bg-yellow-500/15 text-yellow-400"
                    }`}
                  >
                    <i
                      className={
                        photoTimeframeCheck.outOfRange === 0
                          ? "ri-check-line"
                          : "ri-error-warning-line"
                      }
                    />
                    {photoTimeframeCheck.outOfRange === 0
                      ? "Verificado"
                      : "Revisar"}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* ── COMPARACIÓN DE TIPO DE AGUA ── */}
          <SecLabel>Tipo de Agua — Inspección vs. Unidad</SecLabel>
          {(() => {
            const cfg = {
              match: {
                wrap: "border-green-500/40 bg-green-500/10",
                badge: "bg-green-500/15 text-green-400",
                icon: "ri-checkbox-circle-line",
                label: "Coincide",
              },
              mismatch: {
                wrap: "border-red-500/40 bg-red-500/10",
                badge: "bg-red-500/15 text-red-400",
                icon: "ri-close-circle-line",
                label: "No coincide",
              },
              no_unit: {
                wrap: "border-yellow-500/40 bg-yellow-500/10",
                badge: "bg-yellow-500/15 text-yellow-400",
                icon: "ri-error-warning-line",
                label: "Sin unidad asignada",
              },
              no_water: {
                wrap: border,
                badge: `${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`,
                icon: "ri-question-line",
                label: "Sin agua registrada",
              },
            }[waterComparison.state];
            return (
              <div className={`mb-4 p-4 rounded-xl border ${cfg.wrap}`}>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <p
                    className={`text-[10px] uppercase tracking-widest font-bold ${textSec}`}
                  >
                    Compatibilidad
                  </p>
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${cfg.badge}`}
                  >
                    <i className={cfg.icon} />
                    {cfg.label}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Agua inspeccionada</label>
                    {waterComparison.inspected ? (
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg ${surface} ${text}`}
                      >
                        <i className="ri-drop-line text-primary-500" />
                        {getWaterTypeLabel(waterComparison.inspected)}
                      </span>
                    ) : (
                      <p className={`text-sm italic ${textSec}`}>
                        No registrada — selecciónala en «Ver / Completar»
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={lbl}>Tipos asignados a la unidad</label>
                    {waterComparison.state === "no_unit" ? (
                      <p className={`text-sm italic ${textSec}`}>
                        Esta inspección no tiene una unidad asignada
                      </p>
                    ) : waterComparison.vehicleTypes.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {waterComparison.vehicleTypes.map((wt) => {
                          const isMatch = wt === waterComparison.inspected;
                          return (
                            <span
                              key={wt}
                              className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg ${
                                isMatch
                                  ? "bg-green-500/15 text-green-400"
                                  : `${surface} ${textSec}`
                              }`}
                            >
                              {isMatch && <i className="ri-check-line" />}
                              {getWaterTypeLabel(wt)}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className={`text-sm italic ${textSec}`}>
                        La unidad no tiene tipos de agua asignados
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── SELECCIÓN DE PATIO ── */}
          <SecLabel>Patio (heredado del evento de acceso)</SecLabel>
          <div className="mb-4">
            <label className={lbl}>Patio de Maniobras</label>
            <select
              value={selectedYardId}
              onChange={(e) => handleYardChange(e.target.value)}
              className={inp}
              disabled
            >
              <option value="">
                {yardsLoading ? "Cargando patios..." : "— Sin patio —"}
              </option>
              {yards.map((yard) => (
                <option key={yard.id} value={yard.id}>
                  {yard.name} {yard.location ? `(${yard.location})` : ""}
                </option>
              ))}
            </select>
            <p className={`text-xs ${textSec} mt-1`}>
              El patio se hereda del evento de acceso de esta inspección.
            </p>
          </div>

          {/* ── ENCABEZADO ── */}
          <SecLabel>Encabezado del Documento</SecLabel>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {(["left", "right"] as const).map((side) => {
              const preview =
                side === "left" ? logoLeftPreview : logoRightPreview;
              const ref = side === "left" ? refLeft : refRight;
              return (
                <div key={side}>
                  <label className={lbl}>
                    Logo {side === "left" ? "Izquierdo" : "Derecho"} (opcional)
                  </label>
                  <button
                    type="button"
                    onClick={() => ref.current?.click()}
                    className={`w-full h-20 rounded-xl border-2 border-dashed ${border} flex items-center justify-center gap-2 ${textSec} hover:border-primary-500 transition-colors overflow-hidden`}
                  >
                    {preview ? (
                      <img
                        src={preview}
                        alt="logo"
                        className="h-full object-contain p-1"
                      />
                    ) : (
                      <>
                        <i className="ri-image-add-line text-xl" />
                        <span className="text-xs">Subir imagen</span>
                      </>
                    )}
                  </button>
                  <input
                    ref={ref}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogo(side)}
                  />
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
            <div>
              <label className={lbl}>Nombre de la Organización</label>
              <input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className={inp}
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Descripción</label>
              <input
                value={orgDescription}
                onChange={(e) => setOrgDescription(e.target.value)}
                className={inp}
              />
            </div>
          </div>
          <div className="mb-1">
            <label className={lbl}>Folio</label>
            <p
              className={`text-sm font-mono font-bold ${text} px-3 py-2 ${surface} rounded-lg`}
            >
              {folio}
            </p>
          </div>

          <Divider />

          {/* ── ACCESOS ── */}
          <SecLabel>Eventos de Acceso</SecLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Entrada</label>
              <p className={`text-sm ${text} px-3 py-2 ${surface} rounded-lg`}>
                {inspection.entry_timestamp
                  ? new Date(
                      toMillis(inspection.entry_timestamp),
                    ).toLocaleString("es-ES")
                  : "—"}
              </p>
            </div>
            <div>
              <label className={lbl}>Salida</label>
              <p
                className={`text-sm ${inspection.exit_timestamp ? text : "text-yellow-400 italic"} px-3 py-2 ${surface} rounded-lg`}
              >
                {inspection.exit_timestamp
                  ? new Date(
                      toMillis(inspection.exit_timestamp),
                    ).toLocaleString("es-ES")
                  : "Pendiente"}
              </p>
            </div>
          </div>

          <Divider />

          {/* ── SECCIÓN 1 — DATOS DEL VEHÍCULO ── */}
          <SecLabel>Datos del Vehículo</SecLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Nombre / Identificador de la Unidad</label>
              <input
                value={nombreUnidad}
                onChange={(e) => setNombreUnidad(e.target.value)}
                className={inp}
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Placas</label>
              <input
                value={placas}
                onChange={(e) => setPlacas(e.target.value)}
                className={inp}
                placeholder="Ej. ABC-1234"
                disabled
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div>
              <label className={lbl}>Tipo / Modelo de Vehículo</label>
              <input
                value={tipoVehiculo}
                onChange={(e) => setTipoVehiculo(e.target.value)}
                className={inp}
                placeholder="Ej. Camión, Grúa..."
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Año</label>
              <input
                value={anioVehiculo}
                onChange={(e) => setAnioVehiculo(e.target.value)}
                className={inp}
                placeholder="Ej. 2022"
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Tipo de Combustible</label>
              <input
                value={tipoCombustible}
                onChange={(e) => setTipoCombustible(e.target.value)}
                className={inp}
                placeholder="Ej. Diesel, Gasolina..."
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={lbl}>NIS</label>
              <input
                value={nis}
                onChange={(e) => setNis(e.target.value)}
                className={inp}
                placeholder="NIS de la unidad"
              />
            </div>
            <div>
              <label className={lbl}>Capacidad del Contenedor (m³)</label>
              <input
                value={capacidadContenedor}
                onChange={(e) => setCapacidadContenedor(e.target.value)}
                className={inp}
                placeholder="Ej. 10"
              />
            </div>
          </div>

          <Divider />

          {/* ── SECCIÓN 2 — DATOS DEL INSPECTOR ── */}
          <SecLabel>Datos del Inspector</SecLabel>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>Nombre del Inspector</label>
              <input
                value={nombreInspector}
                onChange={(e) => setNombreInspector(e.target.value)}
                className={inp}
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Correo Electrónico</label>
              <input
                type="email"
                value={emailInspector}
                onChange={(e) => setEmailInspector(e.target.value)}
                className={inp}
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Rol</label>
              <input
                value={rolInspector}
                onChange={(e) => setRolInspector(e.target.value)}
                className={inp}
                disabled
              />
            </div>
          </div>

          <Divider />

          {/* ── SECCIÓN 3 — DATOS DE LA INSPECCIÓN ── */}
          <SecLabel>Datos de la Inspección</SecLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>
                Título de la Inspección <span className="text-red-500">*</span>
              </label>
              <input
                value={tituloInspeccion}
                onChange={(e) => setTituloInspeccion(e.target.value)}
                className={inp}
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Fecha y Hora de la Inspección</label>
              <input
                type="datetime-local"
                value={fechaInspeccion}
                onChange={(e) => setFechaInspeccion(e.target.value)}
                className={inp}
                disabled
              />
            </div>
          </div>
          <div className="mt-4">
            <label className={lbl}>Lugar de la Inspección</label>
            <input
              value={lugarInspeccion}
              onChange={(e) => setLugarInspeccion(e.target.value)}
              className={inp}
              placeholder="Ej. Patio principal, Taller mecánico..."
              disabled
            />
          </div>
          {(descripcionInspeccion || true) && (
            <div className="mt-4">
              <label className={lbl}>Descripción General</label>
              <input
                value={descripcionInspeccion}
                onChange={(e) => setDescripcionInspeccion(e.target.value)}
                className={inp}
                disabled
              />
            </div>
          )}

          <Divider />

          {/* ── SECCIÓN 4 — DATOS DE LA EXTRACCIÓN (auto-completados) ── */}
          <SecLabel>Datos de la Extracción</SecLabel>
          <div className="mt-1">
            <label className={lbl}>Domicilio (patio)</label>
            <input
              value={domicilio}
              onChange={(e) => setDomicilio(e.target.value)}
              className={inp}
              placeholder="Se toma de la ubicación del patio"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={lbl}>Empresa Transportista</label>
              <input
                value={empresaTransportista}
                onChange={(e) => setEmpresaTransportista(e.target.value)}
                className={inp}
                placeholder="Grupo al que pertenece la unidad"
              />
            </div>
            <div>
              <label className={lbl}>Volumen Extraído (L)</label>
              <input
                value={volumenExtraido}
                onChange={(e) => setVolumenExtraido(e.target.value)}
                className={inp}
                placeholder="Se toma de la inspección"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div>
              <label className={lbl}>Agua Reportada</label>
              <select
                value={aguaReportada}
                onChange={(e) => setAguaReportada(e.target.value)}
                className={inp}
              >
                <option value="">—</option>
                {WATER_TYPES.map((wt) => (
                  <option key={wt.value} value={wt.value}>
                    {wt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={lbl}>Agua Extraída</label>
              <select
                value={aguaExtraida}
                onChange={(e) => setAguaExtraida(e.target.value)}
                className={inp}
              >
                <option value="">—</option>
                {WATER_TYPES.map((wt) => (
                  <option key={wt.value} value={wt.value}>
                    {wt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={lbl}>Fecha y Hora de Extracción</label>
              <input
                type="datetime-local"
                value={fechaExtraccion}
                onChange={(e) => setFechaExtraccion(e.target.value)}
                className={inp}
              />
            </div>
          </div>
          {/* Las tres lecturas del medidor. type="text" + inputMode="decimal",
              igual que en la modal de captura y por el mismo motivo: un
              type="number" reporta "" para todo lo que no sea un float válido,
              y la coma decimal de es-MX lo es sólo en la pantalla. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            {WATER_PARAMETERS.map((p) => (
              <div key={p.key}>
                <label className={lbl}>
                  {p.label}
                  {p.unit ? ` (${p.unit})` : ""}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={waterValues[p.key]}
                  onChange={(e) =>
                    setWaterValues((prev) => ({
                      ...prev,
                      [p.key]: e.target.value,
                    }))
                  }
                  className={inp}
                  placeholder="Se toma de la inspección"
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={lbl}>Hora de Entrada</label>
              <input
                value={
                  inspection.entry_timestamp
                    ? new Date(
                        toMillis(inspection.entry_timestamp),
                      ).toLocaleString("es-MX")
                    : "—"
                }
                className={inp}
                disabled
              />
            </div>
            <div>
              <label className={lbl}>Hora de Salida</label>
              <input
                value={
                  inspection.exit_timestamp
                    ? new Date(
                        toMillis(inspection.exit_timestamp),
                      ).toLocaleString("es-MX")
                    : "Pendiente"
                }
                className={inp}
                disabled
              />
            </div>
          </div>

          <ThinDivider />

          {/* ── VERIFICACIÓN DE IMÁGENES ── */}
          <div id="verificacion-imagenes" className="scroll-mt-4" />
          <SecLabel>Verificación de imágenes ({photos.length})</SecLabel>
          {photos.length === 0 ? (
            <p className={`text-sm italic ${textSec}`}>
              No se capturaron fotografías en esta inspección.
            </p>
          ) : (
            /* Grid mirroring the inspection modal: image + título + descripción/valor. */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {photos.map((p, i) => (
                <div
                  key={p.id}
                  className={`flex flex-col rounded-xl border ${border} ${surface} overflow-hidden`}
                >
                  {p.url ? (
                    <img
                      src={p.url}
                      alt={p.title || `Foto ${p.photoNumber}`}
                      className="w-full h-40 object-cover"
                    />
                  ) : (
                    <div
                      className={`w-full h-40 flex flex-col items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                    >
                      <i className={`ri-image-line text-3xl ${textSec}`}></i>
                      <span className={`text-[10px] ${textSec}`}>
                        Foto {p.photoNumber}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col gap-2 p-3">
                    <div>
                      <label
                        className={`text-[10px] uppercase ${textSec} block`}
                      >
                        Título
                      </label>
                      <p className={`text-sm font-medium ${text} break-words`}>
                        {p.title || `Foto ${p.photoNumber}`}
                      </p>
                    </div>
                    <div>
                      <label
                        className={`text-[10px] uppercase ${textSec} block`}
                      >
                        Descripción / valor
                      </label>
                      <p className={`text-sm ${text} break-words`}>
                        {p.description || "—"}
                      </p>
                    </div>
                    <div>
                      <label
                        className={`text-[10px] uppercase ${textSec} mb-1 block`}
                      >
                        Observaciones
                      </label>
                      <input
                        value={p.observacion}
                        onChange={(e) =>
                          updatePhoto(i, "observacion", e.target.value)
                        }
                        className={inpPhoto}
                        placeholder="—"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <ThinDivider />

          {/* ── OBSERVACIONES FINALES ── */}
          <SecLabel>Observaciones Generales</SecLabel>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            rows={3}
            className={`${inp} resize-none`}
            placeholder="Observaciones adicionales sobre la inspección..."
            disabled
          />

          {/* ── PREVISUALIZACIÓN PDF ── */}
          {pdfPreviewUrl && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <SecLabel>Previsualización del PDF</SecLabel>
                <button
                  onClick={() => {
                    URL.revokeObjectURL(pdfPreviewUrl);
                    setPdfPreviewUrl(null);
                  }}
                  className={`text-xs px-2 py-1 rounded-lg ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-600 hover:bg-gray-300"} transition-colors`}
                >
                  <i className="ri-close-line mr-1"></i>
                  Cerrar vista previa
                </button>
              </div>
              <div className={`rounded-xl overflow-hidden border ${border}`}>
                <iframe
                  src={pdfPreviewUrl}
                  className="w-full h-[500px]"
                  title="PDF Preview"
                />
              </div>
            </div>
          )}

          {/* ── GENERAR ── */}
          <div className="mt-8 space-y-3">
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <i className="ri-error-warning-line text-red-400" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => handleGenerate(true)}
                disabled={!canGenerate || loading}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all border ${border} ${canGenerate && !loading ? "opacity-100 cursor-pointer" : "opacity-50 cursor-not-allowed"} ${isDarkMode ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-100 text-gray-800 hover:bg-gray-200"}`}
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg" />
                    Generando...
                  </>
                ) : (
                  <>
                    <i className="ri-eye-line text-lg" />
                    Previsualizar PDF
                  </>
                )}
              </button>
              <button
                onClick={() => handleGenerate(false)}
                disabled={!canGenerate || loading}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all ${canGenerate && !loading ? "opacity-100 cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                style={{ backgroundColor: brand.primary, color: brand.accent }}
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg" />
                    Generando...
                  </>
                ) : (
                  <>
                    <i className="ri-download-2-line text-lg" />
                    Descargar PDF
                  </>
                )}
              </button>
            </div>
            {!canGenerate && (
              <p className={`text-center text-xs ${textSec}`}>
                Completa el campo requerido: título de la inspección
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
